import { useState, useRef, useEffect, useCallback } from 'react';
import { Plane, Play, Square, Send, CheckCircle2, AlertCircle, Clock, MapPin, Gauge, ArrowUpDown, Settings, Wifi, WifiOff } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PWAFlightPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem('flight_token') || '');
  const [tokenValid, setTokenValid] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [droneSerial, setDroneSerial] = useState(() => localStorage.getItem('drone_serial') || '');
  const [droneModel, setDroneModel] = useState(() => localStorage.getItem('drone_model') || '');
  const [pilotName, setPilotName] = useState(() => localStorage.getItem('pilot_name') || '');

  // Flight tracking
  const [isFlying, setIsFlying] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [distance, setDistance] = useState('');
  const [maxAlt, setMaxAlt] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingFlights, setPendingFlights] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pending_flights') || '[]'); } catch { return []; }
  });
  const [recentFlights, setRecentFlights] = useState([]);
  const [showSetup, setShowSetup] = useState(true);

  const timerRef = useRef(null);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, []);

  // Timer
  useEffect(() => {
    if (isFlying) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isFlying, startTime]);

  // Save pending flights to localStorage
  useEffect(() => {
    localStorage.setItem('pending_flights', JSON.stringify(pendingFlights));
  }, [pendingFlights]);

  // Validate token
  const validateToken = useCallback(async () => {
    if (!token.trim()) return;
    try {
      const res = await fetch(`${API}/api/flight-data/ingest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Flight-Token': token },
        body: JSON.stringify({ start_time: '', end_time: '' }) // Will get 400 but validates token
      });
      if (res.status === 401) {
        setTokenValid(false);
        toast.error('Token invalido ou revogado');
      } else {
        // Token is valid (got 400 for missing fields, not 401)
        setTokenValid(true);
        localStorage.setItem('flight_token', token);
        toast.success('Token validado!');
        setShowSetup(false);
      }
    } catch {
      toast.error('Sem conexao. Token salvo localmente.');
      localStorage.setItem('flight_token', token);
      setTokenValid(true);
      setShowSetup(false);
    }
  }, [token]);

  // Start flight
  const startFlight = () => {
    const now = Date.now();
    setStartTime(now);
    setElapsed(0);
    setIsFlying(true);
    setDistance('');
    setMaxAlt('');
    setNotes('');
    toast.info('Voo iniciado!');
  };

  // Stop flight and send
  const stopFlight = async () => {
    setIsFlying(false);
    const endTime = new Date().toISOString();
    const stTime = new Date(startTime).toISOString();

    const flightData = {
      start_time: stTime,
      end_time: endTime,
      duration_seconds: elapsed,
      distance_meters: parseFloat(distance) || 0,
      max_altitude_meters: parseFloat(maxAlt) || null,
      drone_serial: droneSerial,
      drone_model: droneModel,
      pilot_name: pilotName,
      notes: notes,
      source: 'pwa_app',
    };

    // Save config
    localStorage.setItem('drone_serial', droneSerial);
    localStorage.setItem('drone_model', droneModel);
    localStorage.setItem('pilot_name', pilotName);

    if (online) {
      await sendFlightData(flightData);
    } else {
      // Store offline
      setPendingFlights(prev => [...prev, flightData]);
      toast.info('Voo salvo localmente. Sera enviado quando houver conexao.');
    }
  };

  const sendFlightData = async (data) => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/flight-data/ingest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Flight-Token': token },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        const result = await res.json();
        toast.success('Dados enviados!');
        setRecentFlights(prev => [{ ...data, id: result.flight_id, sent: true }, ...prev.slice(0, 9)]);
        return true;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao enviar');
        return false;
      }
    } catch {
      toast.error('Sem conexao');
      return false;
    } finally { setSending(false); }
  };

  // Sync pending flights when online
  const syncPending = async () => {
    if (pendingFlights.length === 0) return;
    setSending(true);
    let synced = 0;
    const remaining = [];
    for (const f of pendingFlights) {
      const ok = await sendFlightData(f);
      if (ok) synced++;
      else remaining.push(f);
    }
    setPendingFlights(remaining);
    if (synced > 0) toast.success(`${synced} voo(s) sincronizado(s)`);
    setSending(false);
  };

  useEffect(() => {
    if (online && pendingFlights.length > 0 && tokenValid) syncPending();
  }, [online, tokenValid]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  // ===== SETUP SCREEN =====
  if (showSetup) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex flex-col" data-testid="pwa-setup">
        <Toaster position="top-center" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">
                <Plane size={28} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold tracking-wide">DRONE FLIGHT DATA</h1>
              <p className="text-white/40 text-sm mt-1">monitoramentorpas.com</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider block mb-1">Token do Projeto *</label>
                <input value={token} onChange={e => setToken(e.target.value)}
                  className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-sm font-mono text-white focus:border-amber-500 focus:outline-none placeholder:text-white/20"
                  placeholder="fdt_xxxxxxxxxxxxxxxx" data-testid="pwa-token-input" />
                {tokenValid === false && <p className="text-red-400 text-xs mt-1">Token invalido</p>}
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider block mb-1">Serial do Drone</label>
                <input value={droneSerial} onChange={e => setDroneSerial(e.target.value)}
                  className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-sm text-white focus:border-amber-500 focus:outline-none placeholder:text-white/20"
                  placeholder="Ex: 1581F6Q8D246Q00193D1" data-testid="pwa-drone-serial" />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider block mb-1">Modelo do Drone</label>
                <input value={droneModel} onChange={e => setDroneModel(e.target.value)}
                  className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-sm text-white focus:border-amber-500 focus:outline-none placeholder:text-white/20"
                  placeholder="Ex: DJI Matrice 30T" data-testid="pwa-drone-model" />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider block mb-1">Nome do Piloto</label>
                <input value={pilotName} onChange={e => setPilotName(e.target.value)}
                  className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-sm text-white focus:border-amber-500 focus:outline-none placeholder:text-white/20"
                  placeholder="Seu nome" data-testid="pwa-pilot-name" />
              </div>
            </div>

            <button onClick={validateToken} disabled={!token.trim()}
              className="w-full h-14 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-xl text-sm uppercase tracking-wider disabled:opacity-50 active:scale-[0.98] transition-transform shadow-lg shadow-amber-500/30"
              data-testid="pwa-validate-btn">
              CONECTAR
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== MAIN FLIGHT SCREEN =====
  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white flex flex-col" data-testid="pwa-main">
      <Toaster position="top-center" />
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white/5 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Plane size={18} className="text-amber-400" />
          <span className="text-sm font-bold tracking-wide">FLIGHT DATA</span>
        </div>
        <div className="flex items-center gap-3">
          {pendingFlights.length > 0 && (
            <span className="text-amber-400 text-xs font-mono flex items-center gap-1">
              <AlertCircle size={12} /> {pendingFlights.length} pendente(s)
            </span>
          )}
          <span className={`flex items-center gap-1 text-xs ${online ? 'text-green-400' : 'text-red-400'}`}>
            {online ? <Wifi size={12} /> : <WifiOff size={12} />}
            {online ? 'Online' : 'Offline'}
          </span>
          <button onClick={() => setShowSetup(true)} className="text-white/40 hover:text-white/80">
            <Settings size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-4 space-y-4 overflow-y-auto">
        {/* Timer Display */}
        <div className="text-center py-4">
          <div className={`text-6xl font-mono font-bold tracking-widest ${isFlying ? 'text-green-400' : elapsed > 0 ? 'text-amber-400' : 'text-white/30'}`} data-testid="pwa-timer">
            {formatTime(elapsed)}
          </div>
          <p className="text-white/30 text-xs mt-2 uppercase tracking-wider">
            {isFlying ? 'Voo em andamento' : elapsed > 0 ? 'Voo encerrado' : 'Pronto para voar'}
          </p>
          <p className="text-white/20 text-[10px] font-mono mt-1">
            {droneModel} | {pilotName}
          </p>
        </div>

        {/* Flight Controls */}
        <div className="flex justify-center gap-4">
          {!isFlying ? (
            <button onClick={startFlight}
              className="w-32 h-32 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex flex-col items-center justify-center shadow-lg shadow-green-500/30 active:scale-95 transition-transform"
              data-testid="pwa-start-btn">
              <Play size={40} className="text-white ml-1" />
              <span className="text-white text-xs font-bold mt-1 uppercase">Iniciar</span>
            </button>
          ) : (
            <button onClick={stopFlight}
              className="w-32 h-32 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex flex-col items-center justify-center shadow-lg shadow-red-500/30 active:scale-95 transition-transform animate-pulse"
              data-testid="pwa-stop-btn">
              <Square size={36} className="text-white" />
              <span className="text-white text-xs font-bold mt-1 uppercase">Encerrar</span>
            </button>
          )}
        </div>

        {/* Post-flight data entry */}
        {!isFlying && elapsed > 0 && (
          <div className="space-y-3 bg-white/5 rounded-2xl p-4 border border-white/10" data-testid="pwa-post-flight">
            <p className="text-amber-400 text-xs font-bold uppercase tracking-wider">Dados complementares</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-[10px] uppercase flex items-center gap-1"><MapPin size={10} /> Distancia (m)</label>
                <input value={distance} onChange={e => setDistance(e.target.value)} type="number"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:border-amber-500 focus:outline-none"
                  placeholder="0" data-testid="pwa-distance" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase flex items-center gap-1"><ArrowUpDown size={10} /> Alt. Max (m)</label>
                <input value={maxAlt} onChange={e => setMaxAlt(e.target.value)} type="number"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:border-amber-500 focus:outline-none"
                  placeholder="0" data-testid="pwa-max-alt" />
              </div>
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase">Observacoes</label>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:border-amber-500 focus:outline-none"
                placeholder="Ex: Inspecao torre 12" data-testid="pwa-notes" />
            </div>
          </div>
        )}

        {/* Manual entry */}
        {!isFlying && elapsed === 0 && (
          <div className="bg-white/5 rounded-2xl p-4 border border-white/10 space-y-3" data-testid="pwa-manual-entry">
            <p className="text-white/50 text-xs font-bold uppercase tracking-wider">Ou registre voo manual</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-[10px] uppercase">Duracao (min)</label>
                <input id="manual-duration" type="number"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:border-amber-500 focus:outline-none"
                  placeholder="30" data-testid="pwa-manual-duration" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase">Distancia (m)</label>
                <input id="manual-distance" type="number"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:border-amber-500 focus:outline-none"
                  placeholder="5000" data-testid="pwa-manual-distance" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase">Alt. Max (m)</label>
                <input id="manual-alt" type="number"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:border-amber-500 focus:outline-none"
                  placeholder="120" data-testid="pwa-manual-alt" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase">Observacoes</label>
                <input id="manual-notes"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:border-amber-500 focus:outline-none"
                  placeholder="Nota" data-testid="pwa-manual-notes" />
              </div>
            </div>
            <button onClick={async () => {
              const dur = parseFloat(document.getElementById('manual-duration').value) || 0;
              const dist = parseFloat(document.getElementById('manual-distance').value) || 0;
              const alt = parseFloat(document.getElementById('manual-alt').value) || null;
              const nt = document.getElementById('manual-notes').value || '';
              if (dur === 0 && dist === 0) { toast.error('Preencha duracao ou distancia'); return; }
              const now = new Date();
              const start = new Date(now.getTime() - dur * 60 * 1000);
              const data = {
                start_time: start.toISOString(), end_time: now.toISOString(),
                duration_seconds: dur * 60, distance_meters: dist,
                max_altitude_meters: alt, drone_serial: droneSerial,
                drone_model: droneModel, pilot_name: pilotName,
                notes: nt, source: 'pwa_app'
              };
              if (online) { await sendFlightData(data); }
              else { setPendingFlights(prev => [...prev, data]); toast.info('Salvo localmente'); }
            }} disabled={sending}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-xl text-sm uppercase disabled:opacity-50 active:scale-[0.98] transition-transform"
              data-testid="pwa-manual-send">
              <Send size={14} className="inline mr-2" /> {sending ? 'ENVIANDO...' : 'REGISTRAR VOO'}
            </button>
          </div>
        )}

        {/* Recent flights */}
        {recentFlights.length > 0 && (
          <div className="space-y-2">
            <p className="text-white/30 text-xs uppercase tracking-wider">Voos recentes</p>
            {recentFlights.map((f, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-white text-xs font-medium">{new Date(f.start_time).toLocaleTimeString('pt-BR')}</p>
                  <p className="text-white/40 text-[10px]">{formatTime(f.duration_seconds || 0)} | {f.distance_meters}m</p>
                </div>
                <CheckCircle2 size={16} className="text-green-400" />
              </div>
            ))}
          </div>
        )}

        {/* Sync button for pending */}
        {pendingFlights.length > 0 && online && (
          <button onClick={syncPending} disabled={sending}
            className="w-full h-10 bg-amber-500/20 border border-amber-500/30 text-amber-400 font-bold rounded-xl text-xs uppercase disabled:opacity-50"
            data-testid="pwa-sync-btn">
            <Send size={12} className="inline mr-2" /> SINCRONIZAR {pendingFlights.length} VOO(S) PENDENTE(S)
          </button>
        )}
      </div>
    </div>
  );
};

export default PWAFlightPage;
